import * as APIHelper from "services/api"
import * as endPoint from "shared/constants/endpoint"
import { transformObjectKeys } from "utils/app-helper"
import moment from "moment"
import { detailsKeys, reorderedKeys } from "pages/inbound-delivery/constants"
import { parseDate, formatSavingDate } from "utils/date-helper"

class InboundDeliveryBusiness {
  getData = async searchParams => {
    const options = { params: searchParams }
    const response = (await APIHelper.getRequest(`${endPoint.inbound.list}`, options)) || {}
    return (response?.resource || []).map(x => {
      return { ...x, RECEIPT_VARIANCE: parseInt(x.RECEIPTED_QUANTITY - x.EXPECTED_QUANTITY, 10) }
    })
  }

  getDeliveryInformation = async params => {
    const options = { params }
    const response = await APIHelper.getRequest(`${endPoint.receipt.getDeliveryInformation}`, options)
    let result = transformObjectKeys(Array.isArray(response?.resource) && response.resource.length > 0 ? response.resource[0] : null)
    return result
  }

  getProductInformation = async params => {
    const options = { params }
    const response = await APIHelper.getRequest(`${endPoint.receipt.getProductInformation}`, options)
    return transformObjectKeys(response?.resource)
  }

  headerMapping = sourceFields => {
    const itemPayload = {}

    if (sourceFields) {
      itemPayload.erp_order = sourceFields.erp_order || ""
      itemPayload.receipt_id = sourceFields.receipt_id
      itemPayload.receipt_type = sourceFields.receipt_type
      itemPayload.scheduled_date = moment(sourceFields.scheduled_date).format("MM/DD/YYYY")
      itemPayload.trailer_id = sourceFields.trailer_id || ""
      itemPayload.source = sourceFields.source || ""
      itemPayload.warehouse = sourceFields.warehouse
    }

    return itemPayload
  }

  detailsMapping = (sourceFields, action) => {
    if (sourceFields) {
      const item = {
        action: action,
        erp_order_line_num: parseInt(sourceFields.line_num),
        erp_order: sourceFields.erp_order || "",
        item: sourceFields.product,
        quantity: parseFloat(sourceFields.expected_quantity),
        quantity_um: "EA",
      }

      if (sourceFields.lot) item.lot = sourceFields.lot
      if (sourceFields.expiration_date_time) item.expiration_date_time = formatSavingDate(sourceFields.expiration_date_time)

      return item
    }
  }

  productNewDelete = (products, deletedProducts, oldProducts) => {
    const list = []
    const productList = products?.filter(item => item.action === "NEW")

    if (productList) {
      productList.forEach((element, idx) => {
        const item = this.detailsMapping(element, "NEW")
        list.push(item)

        const oldProduct = oldProducts?.find(item => item.line_num === element.line_num)
        if (oldProduct) {
          const item = this.detailsMapping(oldProduct, "DELETE")
          list.push(item)
        }
      })
    }

    if (deletedProducts) {
      deletedProducts.forEach((element, idx) => {
        const existProduct = productList?.find(item => item.line_num === element.line_num)
        if (!existProduct) {
          const item = this.detailsMapping(element, "DELETE")
          list.push(item)
        }
      })
    }

    return list
  }

  productChange = (products, oldProducts) => {
    const list = []
    const productList = products?.filter(item => item.action === "CHANGE")
    if (!productList) {
      return null
    }

    productList.forEach((element, idx) => {
      const oldProduct = oldProducts?.filter(item => item.line_id === element.line_id)

      if (parseInt(element.line_num) === parseInt(oldProduct[0]["line_num"])) {
        if (element.ERP_ORDER) {
          if (element.ERP_ORDER !== oldProduct[0]["ERP_ORDER"]) {
            let itemDelete = this.detailsMapping(oldProduct[0], "DELETE")
            list.push(itemDelete)

            itemDelete = this.detailsMapping(element, "NEW")
            list.push(itemDelete)
          }
        } else {
          const itemChange = this.detailsMapping(element, "CHANGE")
          list.push(itemChange)
        }
      } else {
        let itemElse = this.detailsMapping(oldProduct[0], "DELETE")
        list.push(itemElse)

        itemElse = this.detailsMapping(element, "NEW")
        list.push(itemElse)
      }
    })

    return list
  }

  addData = async data => {
    const itemPayload = this.headerMapping(data)
    const details = []
    data.products.forEach(item => {
      details.push(this.detailsMapping(item, "NEW"))
    })
    itemPayload.details = details

    const payload = { receipts: [itemPayload] }

    const response = await APIHelper.postRequest(`${endPoint.receipt.post}`, payload)
    return response
  }

  updateData = async data => {
    const arrayPayload = []
    // ===================================================================
    const itemPayload = this.headerMapping(data)
    // ===================================================================
    const deletedProducts = data.products?.filter(x => x.action == "DELETE")
    const productNewDelete = this.productNewDelete(data.products, deletedProducts, data.oldProducts)
    const productChange = this.productChange(data.products, data.oldProducts)
    // ===================================================================
    itemPayload.details = [...productNewDelete, ...productChange]
    // ===================================================================

    arrayPayload.push(itemPayload)
    const payload = { receipts: arrayPayload }

    const response = await APIHelper.patchRequest(`${endPoint.receipt.put}`, payload)

    const newDetails = itemPayload.details.filter(x => x.action == "NEW" && itemPayload.details.find(y => y.action == "DELETE" && x.erp_order_line_num == y.erp_order_line_num))
    if (newDetails.length > 0) {
      itemPayload.details = itemPayload.details.filter(x => x.action != "NEW" || !newDetails.find(y => x.erp_order_line_num == y.erp_order_line_num))

      await APIHelper.patchRequest(`${endPoint.receipt.put}`, { receipts: [{ ...itemPayload, details: newDetails }] })
    }

    return response
  }

  deleteData = async params => {
    return await APIHelper.deleteRequest(`${endPoint.receipt.delete}`, params)
  }

  getUploadModel = data => {
    const reorderedData = {}
    const reorderedDetail = {}
    reorderedKeys.forEach(item => {
      const val = data[item] || ""
      reorderedData[item] = val
      if (item.includes("date") && val) {
        reorderedData[item] = moment(parseDate(val)).format("MM/DD/YYYY") || ""
      }
    })

    detailsKeys.forEach(item => {
      if (item === "quantity") {
        reorderedDetail[item] = !data[item] ? 0 : parseFloat(data[item])
      } else {
        reorderedDetail[item] = data[item]
      }
    })
    reorderedDetail.action = "NEW"

    reorderedData["details"] = []
    reorderedData["details"].push(reorderedDetail)
    reorderedData["details"][0]["erp_order_line_num"] = 1
    reorderedData["details"][0]["erp_order"] = data["erp_order"]
    reorderedData["details"][0]["quantity_um"] = "EA"

    if (!reorderedData["scheduled_ship_date"]) {
      delete reorderedData["scheduled_ship_date"]
    }
    if (!reorderedData["receipt_date"]) {
      delete reorderedData["receipt_date"]
    }

    return reorderedData
  }

  getCombineData = addData => {
    if (!Array.isArray(addData)) return []

    // Step 1. Group items by (receipt_id + warehouse)
    const groups = new Map()

    for (const item of addData) {
      const key = `${item.receipt_id || ""}__${item.warehouse || ""}`
      if (!groups.has(key)) {
        groups.set(key, [])
      }
      groups.get(key).push(item)
    }

    // Step 2. Merge each group’s details
    const combined = []

    for (const [key, items] of groups.entries()) {
      if (items.length === 0) continue

      // Merge details from all records
      const mergedDetails = items.flatMap(it => it.details || [])

      // Reassign erp_order_line_num sequentially
      mergedDetails.forEach((d, i) => {
        d.erp_order_line_num = i + 1
      })

      // Use the first item as the base
      const baseItem = { ...items[0], details: mergedDetails }

      combined.push(baseItem)
    }

    return combined
  }

  uploadData = async data => {
    const modelData = data.map(x => this.getUploadModel(x))
    let payload = {}

    const combineData = this.getCombineData(modelData)
    if (combineData) {
      payload = {
        receipts: combineData,
      }
      payload.receipts.forEach((item, id) => {
        if (item === null) {
          payload.receipts.splice(id, 1)
        }
      })
    } else {
      payload = {
        receipts: modelData,
      }
    }

    return await APIHelper.postRequest(`${endPoint.receipt.post}`, payload)
  }
}

export const inboundDeliveryBusiness = new InboundDeliveryBusiness()
